import {COLOR_LAYER_MAP, toRgbaString} from '@core_x6/lib/draw/ArchimateRendererUtil';
import {
	ARCHIMATE_GROUP,
	ASPECT_MOTIVATION,
	OTHER_GROUPING,
	OTHER_LOCATION,
} from '@core_x6/lib/metamodel/Concept';
import {
	AND_JUNCTION,
	getAspectType,
	getLayerType,
	NOTE,
	OR_JUNCTION,
} from '@core_x6/lib/util/ModelUtil';
import {STICKER} from '@core_x6/lib/util/CanvasNodeTypes';

/** Узел/связь в JSON модели редактора (см. JsonSerializer.modelToJson). */
type JsonNode = {
	identifier?: string;
	type?: string;
	name?: string;
	label?: string;
	/** id концепта в model.elements (см. JsonSerializer.nodeToJson) */
	elementRef?: string | null;
	children?: JsonNode[];
};

type JsonConnection = {
	identifier?: string;
	type?: string;
	name?: string;
	source?: string | null;
	target?: string | null;
};

type JsonElement = {
	identifier?: string;
	name?: string;
};

type DiagramJson = {
	nodes?: JsonNode[];
	connections?: JsonConnection[];
};

type ModelJson = {diagrams?: DiagramJson[]; elements?: JsonElement[]};

export type CanvasSearchGlyphShape = 'rect' | 'roundRect' | 'hex' | 'circle' | 'connector';

export type CanvasSearchRowMeta = {
	kind: 'node' | 'connection';
	name: string;
	typeLabel: string;
	fill: string;
	line: string;
	shape: CanvasSearchGlyphShape;
};

function parseModel(initialJson: object | string | undefined): ModelJson | null {
	if (initialJson == null) return null;
	if (typeof initialJson === 'string') {
		try {
			return JSON.parse(initialJson) as ModelJson;
		} catch {
			return null;
		}
	}
	return initialJson as ModelJson;
}

function buildConceptNameMap(model: ModelJson | null): Map<string, string> {
	const out = new Map<string, string>();
	if (!model?.elements?.length) return out;
	for (const el of model.elements) {
		if (!el?.identifier) continue;
		const name = typeof el.name === 'string' ? el.name.trim() : '';
		if (name) out.set(el.identifier, name);
	}
	return out;
}

/** Подпись узла на диаграмме: label, имя на ноде, имя концепта из model.elements, иначе тип. */
function nodePrimaryLabel(n: JsonNode, conceptNames: Map<string, string>): string {
	const label = n.label?.trim();
	if (label) return label;
	const inline = typeof n.name === 'string' ? n.name.trim() : '';
	if (inline) return inline;
	const ref = typeof n.elementRef === 'string' ? n.elementRef.trim() : '';
	if (ref) {
		const concept = conceptNames.get(ref)?.trim();
		if (concept) return concept;
	}
	return n.type?.trim() || 'Элемент';
}

function glyphForArchimateNodeType(
	type: string
): Pick<CanvasSearchRowMeta, 'fill' | 'line' | 'shape'> {
	const t = type.trim();
	if (t === AND_JUNCTION) {
		return {fill: '#000000', line: '#000000', shape: 'circle'};
	}
	if (t === OR_JUNCTION) {
		return {fill: 'transparent', line: '#000000', shape: 'circle'};
	}
	if (t === NOTE) {
		return {fill: '#ffffc0', line: '#333333', shape: 'roundRect'};
	}
	if (t === STICKER) {
		return {fill: '#FFF9B1', line: 'transparent', shape: 'roundRect'};
	}
	if (t === OTHER_GROUPING || t === ARCHIMATE_GROUP) {
		return {fill: '#c0c0c0', line: '#585858', shape: 'roundRect'};
	}
	if (t === OTHER_LOCATION) {
		return {fill: '#fff8dc', line: '#333333', shape: 'rect'};
	}
	const aspect = getAspectType(t);
	if (aspect === ASPECT_MOTIVATION) {
		const layer = getLayerType(t);
		const c = layer ? COLOR_LAYER_MAP.get(layer) : null;
		const fill = (c && toRgbaString(c)) || 'rgba(204, 204, 255, 1)';
		return {fill: String(fill), line: '#333333', shape: 'hex'};
	}
	const layer = getLayerType(t);
	const c = layer ? COLOR_LAYER_MAP.get(layer) : null;
	const fill = (c && toRgbaString(c)) || 'rgba(255, 255, 255, 1)';
	return {fill: String(fill), line: '#333333', shape: 'roundRect'};
}

function fillNodeSearchMeta(
	nodes: JsonNode[] | undefined,
	primaryById: Map<string, string>,
	metaMap: Map<string, CanvasSearchRowMeta>,
	conceptNames: Map<string, string>
): void {
	if (!nodes?.length) return;
	for (const n of nodes) {
		if (!n?.identifier) continue;
		const id = n.identifier;
		const typeLabel = n.type?.trim() || 'Элемент';
		const name = nodePrimaryLabel(n, conceptNames);
		primaryById.set(id, name);
		const glyph = glyphForArchimateNodeType(typeLabel);
		metaMap.set(id, {kind: 'node', name, typeLabel, ...glyph});
		fillNodeSearchMeta(n.children, primaryById, metaMap, conceptNames);
	}
}

/**
 * Метаданные строки результата поиска: имя, тип ArchiMate, мини-фигура (цвет/форма).
 */
export function buildCanvasSearchRowMetaMap(
	initialJson: object | string | undefined,
	viewIndex: number
): Map<string, CanvasSearchRowMeta> {
	const metaMap = new Map<string, CanvasSearchRowMeta>();
	const model = parseModel(initialJson);
	const diagrams = model?.diagrams;
	if (!diagrams?.length) return metaMap;
	const idx = Math.min(Math.max(0, viewIndex), diagrams.length - 1);
	const view = diagrams[idx];
	const conceptNames = buildConceptNameMap(model);

	const primaryById = new Map<string, string>();
	fillNodeSearchMeta(view.nodes, primaryById, metaMap, conceptNames);

	const conns = view.connections;
	if (!conns?.length) return metaMap;

	for (const c of conns) {
		if (!c?.identifier) continue;
		const typeLabel = c.type?.trim() || 'Связь';
		const src = (c.source && primaryById.get(c.source)) || c.source || '?';
		const tgt = (c.target && primaryById.get(c.target)) || c.target || '?';
		const arrow = `${src} → ${tgt}`;
		const connName = typeof c.name === 'string' && c.name.trim() ? c.name.trim() : arrow;
		metaMap.set(c.identifier, {
			kind: 'connection',
			name: connName,
			typeLabel,
			fill: 'transparent',
			line: '#555555',
			shape: 'connector',
		});
	}
	return metaMap;
}

export function getCanvasSearchRowMeta(
	map: Map<string, CanvasSearchRowMeta>,
	id: string
): CanvasSearchRowMeta {
	return (
		map.get(id) ?? {
			kind: 'node',
			name: id,
			typeLabel: 'Элемент',
			fill: '#f5f5f5',
			line: '#999999',
			shape: 'rect',
		}
	);
}

/**
 * Подписи к canvas id для текущего view (краткая строка).
 */
export function buildCanvasIdDisplayMap(
	initialJson: object | string | undefined,
	viewIndex: number
): Map<string, string> {
	const meta = buildCanvasSearchRowMetaMap(initialJson, viewIndex);
	const map = new Map<string, string>();
	for (const [id, row] of meta) {
		map.set(id, `${row.name} (${row.typeLabel})`);
	}
	return map;
}
